import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  Link,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Save as SaveIcon, KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import productService from '../../services/productService';
import categoryService from '../../services/categoryService';
import unitService from '../../services/unitService';
import { getCurrentUser } from '../../services/authService';
import { getTaxDropdownLabelWithRate } from '../../utils/taxDisplay';

// Column widths (px) - Qty, MRP, prices wider; UOM narrow (abbreviation only)
const COL_WIDTHS = {
  name: 200,
  hsnCode: 90,
  categoryId: 160,
  quantity: 95,
  unitId: 65,
  taxId: 120,
  mrp: 95,
  purchasePrice: 105,
  sellingPrice: 105,
  sellingPriceWithTax: 95,
  wholesalePrice: 105,
  wholesalePriceWithTax: 95,
};

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: '#2c3e50',
  color: '#fff',
  border: '1px solid #34495e',
  padding: '10px 12px',
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  zIndex: 2,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
}));

// Kept for compatibility; Product Name column no longer sticky so all columns scroll together
const stickyProductNameCellSx = (bgcolor) => ({ backgroundColor: bgcolor || '#fff' });

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  border: '1px solid #bdc3c7',
  padding: '6px 10px',
  backgroundColor: theme.palette.background.paper,
  verticalAlign: 'middle',
  '& .MuiInput-root': { backgroundColor: 'transparent' },
  '& .MuiInput-input': { padding: 0 },
}));

/**
 * Quick Product Entry - Excel-style grid for add/update products.
 * Uses same createProduct/updateProduct APIs as Products page.
 * No delete - use existing Products page to make inactive.
 * New products are created as ACTIVE; use Full Product Form to change status.
 */
const QuickProductEntryPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const user = getCurrentUser();
  const companyId = user?.companyId || user?.company_id || 1;

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirtyRowIds, setDirtyRowIds] = useState(new Set());
  const [openDropdown, setOpenDropdown] = useState(null);
  const openDropdownRef = useRef(null); // synchronous: so second Enter (select first option) is not treated as "move"
  const cellRefs = useRef({});
  const [focusedCell, setFocusedCell] = useState({ rowIdx: null, colIdx: null });

  const COLUMN_KEYS = ['name', 'hsnCode', 'categoryId', 'quantity', 'unitId', 'taxId', 'mrp', 'purchasePrice', 'sellingPrice', 'sellingPriceWithTax', 'wholesalePrice', 'wholesalePriceWithTax'];
  const COL_COUNT = COLUMN_KEYS.length;

  const noSpinnerSx = {
    '& input[type=number]': { MozAppearance: 'textfield' },
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
  };

  // Prevent mouse wheel from changing numeric values when scrolling table
  const handleNumberWheel = (e) => {
    if (e.target?.type === 'number') {
      e.preventDefault();
    }
  };

  // No extra white box: content displays directly in the column
  const cellFieldSx = {
    '& .MuiInput-root': { backgroundColor: 'transparent' },
    '& .MuiInput-input': { padding: 0 },
  };
  const cellSelectSx = {
    '& .MuiInput-root': { backgroundColor: 'transparent' },
    '& .MuiInput-input': { padding: 0 },
    '& .MuiSelect-select': { padding: 0 },
  };

  const dropdownFocusSx = {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: 2,
      },
    },
  };

  // Cursor identification: highlight focused cell like POS (light blue + outline)
  const getCellStyle = useCallback((rowIdx, colIdx, row) => {
    const isFocused = focusedCell?.rowIdx === rowIdx && focusedCell?.colIdx === colIdx;
    const rowBg = !row.id ? '#e8f5e9' : dirtyRowIds.has(row.id) ? '#fff8e1' : (rowIdx % 2 === 0 ? '#ffffff' : '#f0f4f8');
    return {
      minWidth: COL_WIDTHS[COLUMN_KEYS[colIdx]],
      backgroundColor: isFocused ? '#e3f2fd' : rowBg,
      outline: isFocused ? '2px solid #1976d2' : 'none',
      outlineOffset: -2,
      zIndex: isFocused ? 1 : 0,
      position: 'relative',
    };
  }, [focusedCell, dirtyRowIds]);

  // Dropdown styling: clear look with arrow icon
  const selectSx = {
    ...cellSelectSx,
    '& .MuiSelect-select': { padding: '2px 24px 2px 0', minHeight: 24 },
    '& .MuiSelect-icon': { color: 'text.secondary' },
  };

  // Fetch lookup data
  const fetchLookups = useCallback(async () => {
    try {
      const [catRes, unitsRes, taxesData] = await Promise.all([
        categoryService.getAllCategories({ page: 0, size: 500 }),
        unitService.getAllUnits({ page: 0, size: 100 }),
        productService.getTaxes(),
      ]);
      setCategories(catRes?.content || catRes || []);
      setUnits(unitsRes?.content || []);
      setTaxes(Array.isArray(taxesData) ? taxesData : (taxesData?.content || []));
    } catch (e) {
      console.error('Failed to load lookups', e);
      enqueueSnackbar('Failed to load categories/units/taxes', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // Fetch products and build rows
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      // 1) Load base products from products table
      const res = await productService.getAllProducts(0, 2000, 'name', 'asc');
      const list = res?.content || res || [];

      // 2) For location users, load quantities from location_products and override product quantity
      let locationQuantityMap = null;
      try {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        const hasLocation = storedUser?.location?.id != null;

        if (hasLocation) {
          const locationProducts = await productService.getLocationProducts();
          const lpArray = Array.isArray(locationProducts) ? locationProducts : [];
          locationQuantityMap = new Map();
          lpArray.forEach((lp) => {
            if (lp && lp.productId != null) {
              locationQuantityMap.set(lp.productId, Number(lp.quantity) || 0);
            }
          });
        }
      } catch (locErr) {
        console.warn('[QuickProductEntryPage] Failed to load location product quantities, using products table quantity instead.', locErr);
      }

      const mapped = list.map((p) => {
        const baseQuantity = p.quantity ?? 0;
        const overriddenQty =
          locationQuantityMap && locationQuantityMap.has(p.id)
            ? locationQuantityMap.get(p.id)
            : baseQuantity;

        return {
          id: p.id,
          sku: p.sku || '',
          name: p.name || '',
          hsnCode: p.hsnCode || '',
          categoryId: p.category?.id ?? p.categoryId ?? '',
          quantity: overriddenQty,
          taxId: p.tax?.id ?? p.taxId ?? '',
          mrp: p.mrp ?? '',
          purchasePrice: p.purchasePrice ?? '',
          sellingPrice: p.sellingPrice ?? '',
          sellingPriceWithTax: '',
          wholesalePrice: p.wholesalePrice ?? '',
          wholesalePriceWithTax: '',
          unitId: p.unit?.id ?? p.unitId ?? '',
          status: p.status || 'ACTIVE',
        };
      });
      // Always append one empty row at bottom for new product entry
      const emptyRow = {
        id: null,
        sku: '',
        name: '',
        hsnCode: '',
        categoryId: '',
        quantity: 0,
        taxId: '',
        mrp: '',
        purchasePrice: '',
        sellingPrice: '',
        sellingPriceWithTax: '',
        wholesalePrice: '',
        wholesalePriceWithTax: '',
        unitId: '',
        status: 'ACTIVE',
      };
      setRows([...mapped, emptyRow]);
      setDirtyRowIds(new Set());
    } catch (e) {
      console.error('Failed to load products', e);
      enqueueSnackbar('Failed to load products', { variant: 'error' });
      setRows([{
        id: null, name: '', hsnCode: '', categoryId: '', quantity: 0, taxId: '',
        mrp: '', purchasePrice: '', sellingPrice: '', sellingPriceWithTax: '',
        wholesalePrice: '', wholesalePriceWithTax: '', unitId: '', status: 'ACTIVE',
      }]);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchLookups();
    fetchProducts();
  }, [fetchLookups, fetchProducts]);

  const getTaxRate = (taxId) => {
    if (!taxId) return 0;
    const t = taxes.find((x) => x.id != null && String(x.id) === String(taxId));
    return t ? (parseFloat(t.rate) || 0) : 0;
    // Normalize to number for id fields so Select value matches MenuItem value
  };

  const handleDropdownChange = (rowIndex, colIdx, field, value) => {
    const normalized =
      (field === 'categoryId' || field === 'unitId' || field === 'taxId') && value !== '' && value != null
        ? Number(value)
        : value;
    handleCellChange(rowIndex, field, normalized);
    // Focus move happens in onClose so that Enter on already-selected option (e.g. None, Active) also moves
  };

  const handleDropdownClose = (rowIdx, colIdx) => {
    openDropdownRef.current = null;
    setOpenDropdown(null);
    const delay = 80;
    if (colIdx < COL_COUNT - 1) {
      setTimeout(() => focusCell(rowIdx, colIdx + 1), delay);
    } else {
      addNewRow();
      setTimeout(() => focusCell(rows.length, 0), 120);
    }
  };

  const handleCellChange = (rowIndex, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      const row = next[rowIndex];
      const newTaxId = field === 'taxId' ? value : row.taxId;
      const rate = getTaxRate(newTaxId);
      const factor = 1 + rate / 100;
      let updates = { [field]: value };
      if (field === 'sellingPriceWithTax') {
        const withTax = parseFloat(value);
        updates.sellingPrice = !isNaN(withTax) && withTax > 0 ? (withTax / factor).toFixed(2) : '';
      } else if (field === 'sellingPrice') {
        const withoutTax = parseFloat(value);
        updates.sellingPriceWithTax = !isNaN(withoutTax) && withoutTax > 0 ? (withoutTax * factor).toFixed(2) : '';
      } else if (field === 'wholesalePriceWithTax') {
        const withTax = parseFloat(value);
        updates.wholesalePrice = !isNaN(withTax) && withTax > 0 ? (withTax / factor).toFixed(2) : '';
      } else if (field === 'wholesalePrice') {
        const withoutTax = parseFloat(value);
        updates.wholesalePriceWithTax = !isNaN(withoutTax) && withoutTax > 0 ? (withoutTax * factor).toFixed(2) : '';
      } else if (field === 'taxId') {
        const sp = parseFloat(row.sellingPrice);
        const wp = parseFloat(row.wholesalePrice);
        updates.sellingPriceWithTax = !isNaN(sp) && sp > 0 ? (sp * factor).toFixed(2) : '';
        updates.wholesalePriceWithTax = !isNaN(wp) && wp > 0 ? (wp * factor).toFixed(2) : '';
      }
      next[rowIndex] = { ...row, ...updates };
      return next;
    });
    const row = rows[rowIndex];
    if (row?.id) {
      setDirtyRowIds((prev) => new Set(prev).add(row.id));
    } else {
      setDirtyRowIds((prev) => new Set(prev).add(`new-${rowIndex}`));
    }
  };

  const emptyRowObj = () => ({
    id: null,
    name: '',
    hsnCode: '',
    categoryId: '',
    quantity: 0,
    taxId: '',
    mrp: '',
    purchasePrice: '',
    sellingPrice: '',
    sellingPriceWithTax: '',
    wholesalePrice: '',
    wholesalePriceWithTax: '',
    unitId: '',
    status: 'ACTIVE',
  });

  const addNewRow = useCallback(() => {
    setRows((prev) => {
      const next = [...prev, emptyRowObj()];
      setDirtyRowIds((p) => new Set(p).add(`new-${prev.length}`));
      return next;
    });
  }, []);

  const focusCell = useCallback((rowIdx, colIdx) => {
    setTimeout(() => {
      const ref = cellRefs.current[`${rowIdx}-${colIdx}`];
      if (ref) {
        const el = ref.querySelector?.('input') || ref.querySelector?.('[role="button"]') || ref.querySelector?.('button');
        if (el?.focus) el.focus();
      }
    }, 0);
  }, []);

  const DROPDOWN_COLS = [2, 4, 5]; // Category, UOM, GST
  const COL_TO_FIELD = { 2: 'categoryId', 4: 'unitId', 5: 'taxId' };

  const focusListboxWhenOpen = useCallback(() => {
    setTimeout(() => {
      const listbox = document.querySelector('.MuiModal-root [role="listbox"]') || document.querySelector('.MuiPopover-root [role="listbox"]');
      if (listbox && typeof listbox.focus === 'function') listbox.focus();
    }, 50);
  }, []);

  const selectHighlightedOptionWithEnter = useCallback((rowIdx, colIdx) => {
    const listbox = document.querySelector('.MuiModal-root [role="listbox"]') || document.querySelector('.MuiPopover-root [role="listbox"]') || Array.from(document.querySelectorAll('[role="listbox"]')).find((lb) => lb.offsetParent != null);
    if (!listbox) return false;
    let option = null;
    const activeId = listbox.getAttribute('aria-activedescendant');
    if (activeId) option = document.getElementById(activeId);
    if (!option && document.activeElement?.getAttribute('role') === 'option' && listbox.contains(document.activeElement)) option = document.activeElement;
    if (!option) option = listbox.querySelector('[role="option"][tabindex="0"]') || listbox.querySelector('[role="option"].Mui-focusVisible');
    if (!option) return false;
    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const index = options.indexOf(option);
    if (index < 0) return false;
    const field = COL_TO_FIELD[colIdx];
    if (!field) return false;
    let value;
    if (colIdx === 2) {
      value = index === 0 ? '' : (categories[index - 1]?.id ?? '');
    } else if (colIdx === 4) {
      value = index === 0 ? '' : (units[index - 1]?.id ?? '');
    } else if (colIdx === 5) {
      value = index === 0 ? '' : (taxes[index - 1]?.id ?? '');
    } else {
      return false;
    }
    handleDropdownChange(rowIdx, colIdx, field, value);
    handleDropdownClose(rowIdx, colIdx);
    return true;
  }, [categories, units, taxes]);

  const handleCellKeyDown = useCallback((rowIdx, colIdx, e) => {
    const key = `${rowIdx}-${colIdx}`;
    const isDropdownCol = DROPDOWN_COLS.includes(colIdx);
    const isThisDropdownOpen = openDropdownRef.current === key;
    const row = rows[rowIdx] || {};
    const productName = String(row.name || '').trim();

    // Block default ArrowUp/ArrowDown behavior (changing numeric value).
    // ArrowDown: jump directly to bottom empty row in same column.
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      if (e.key === 'ArrowDown') {
        const lastIndex = rows.length - 1;
        if (lastIndex >= 0) {
          focusCell(lastIndex, colIdx);
        }
      }
      return;
    }

    if (e.key === 'Enter') {
      // Do not allow moving to next field or creating new rows without product name (keyboard flow)
      if (!productName && colIdx === 0) {
        e.preventDefault();
        enqueueSnackbar('Product name is required before moving to next field.', { variant: 'warning' });
        return;
      }

      if (isDropdownCol && isThisDropdownOpen) {
        e.preventDefault();
        if (selectHighlightedOptionWithEnter(rowIdx, colIdx)) return;
        handleDropdownClose(rowIdx, colIdx);
        return;
      }
      e.preventDefault();
      if (isDropdownCol && openDropdownRef.current == null) {
        setOpenDropdown(key);
        openDropdownRef.current = key;
        return;
      }
      if (colIdx < COL_COUNT - 1) {
        focusCell(rowIdx, colIdx + 1);
      } else {
        // When on the last column, only create a new row if current row has a product name
        if (!productName) {
          enqueueSnackbar('Enter Product name before adding a new row.', { variant: 'warning' });
          return;
        }
        const lastIndex = rows.length - 1;
        const lastRow = rows[lastIndex];
        const lastHasName = String(lastRow?.name || '').trim() !== '';

        // If bottom row already has a product name, create a new one.
        // Otherwise, reuse the existing empty bottom row.
        if (lastHasName) {
          addNewRow();
          setTimeout(() => focusCell(rows.length, 0), 80);
        } else if (lastIndex >= 0) {
          setTimeout(() => focusCell(lastIndex, 0), 80);
        }
      }
      return;
    }
    if (isDropdownCol && (e.key === 'ArrowDown' || e.key === 'ArrowUp') && isThisDropdownOpen) {
      return;
    }
  }, [addNewRow, enqueueSnackbar, focusCell, rows, selectHighlightedOptionWithEnter, handleDropdownClose]);

  // Focus empty row's Product name when data has loaded (cursor defaults to new-entry row)
  useEffect(() => {
    if (!loading && rows.length > 0) {
      const emptyRowIndex = rows.length - 1;
      const t = setTimeout(() => focusCell(emptyRowIndex, 0), 100);
      return () => clearTimeout(t);
    }
  }, [loading, rows.length, focusCell]);

  const buildPayload = (row) => {
    const hsnVal = String(row.hsnCode || '').trim();
    return {
      name: String(row.name || '').trim(),
      hsnCode: hsnVal || null,
      categoryId: row.categoryId ? parseInt(row.categoryId) : null,
      unitId: row.unitId ? parseInt(row.unitId) : null,
      taxId: row.taxId ? parseInt(row.taxId) : null,
      quantity: parseFloat(row.quantity) || 0,
      mrp: row.mrp ? parseFloat(row.mrp) : null,
      purchasePrice: parseFloat(row.purchasePrice) || 0,
      sellingPrice: parseFloat(row.sellingPrice) || 0,
      wholesalePrice: row.wholesalePrice ? parseFloat(row.wholesalePrice) : null,
      minSalePrice: null,
      reorderLevel: null,
      status: row.status || 'ACTIVE',
      companyId: parseInt(companyId),
      manageable: true,
      sku: row.id ? (row.sku || undefined) : null,
      subUnit1Id: null,
      subUnit2Id: null,
    };
  };

  const handleSave = useCallback(async () => {
    const toCreate = rows.filter((r) => !r.id && (r.name || '').trim());
    const toUpdate = rows.filter((r) => r.id && dirtyRowIds.has(r.id));

    if (toCreate.length === 0 && toUpdate.length === 0) {
      enqueueSnackbar('No changes to save', { variant: 'info' });
      return;
    }

    // Validate mandatory fields: Product name, Category, Retailer Rate, UOM
    const invalidRows = [];
    const checkMandatory = (row) => {
      const name = String(row.name || '').trim();
      const hasCategory = !!row.categoryId;
      const hasUom = !!row.unitId;
      const sp = parseFloat(row.sellingPrice);
      const hasRetailerRate = !Number.isNaN(sp) && sp > 0;
      if (!name || !hasCategory || !hasUom || !hasRetailerRate) {
        invalidRows.push(row);
      }
    };
    toCreate.forEach(checkMandatory);
    toUpdate.forEach(checkMandatory);

    if (invalidRows.length > 0) {
      enqueueSnackbar('Product name, Category, Retailer Rate and UOM are required for all rows before saving.', {
        variant: 'warning',
      });
      return;
    }

    setSaving(true);
    let created = 0;
    let updated = 0;
    try {
      for (const row of toCreate) {
        const payload = buildPayload(row);
        await productService.createProduct(payload);
        created++;
      }
      for (const row of toUpdate) {
        const payload = buildPayload(row);
        await productService.updateProduct(row.id, payload);
        updated++;
      }
      if (created > 0 || updated > 0) {
        enqueueSnackbar(
          `Saved: ${created} created, ${updated} updated`,
          { variant: 'success' }
        );
        setDirtyRowIds(new Set());
        fetchProducts();
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [rows, dirtyRowIds, enqueueSnackbar, fetchProducts]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleSave]);

  if (loading) {
    return (
      <Layout>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const totalMinWidth = COLUMN_KEYS.reduce((sum, k) => sum + (COL_WIDTHS[k] || 90), 0);

  return (
    <Layout>
      <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Paper elevation={2} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderBottom: '2px solid #2c3e50', flexShrink: 0 }}>
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              Excel Product Entry
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Link component={RouterLink} to="/inventory/products" underline="hover" sx={{ fontSize: 14 }}>
                Full Product Form
              </Link>
              <Button
                variant="contained"
                color="primary"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || (dirtyRowIds.size === 0 && !rows.some((r) => !r.id && (r.name || '').trim()))}
              >
                Save (Ctrl+S)
              </Button>
            </Box>
          </Box>

          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="small" sx={{ minWidth: totalMinWidth }}>
              <TableHead>
                <TableRow>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.name }}>Product Name *</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.hsnCode }}>HSN</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.categoryId }}>Category *</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.quantity }}>Qty</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.unitId }}>UOM *</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.taxId }}>GST</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.mrp }}>MRP</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.purchasePrice }}>Purchase</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.sellingPrice }}>Retailer *</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.sellingPriceWithTax }}>Ret. w/Tax</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.wholesalePrice }}>Wholesaler</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: COL_WIDTHS.wholesalePriceWithTax }}>Whole. w/Tax</StyledTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow
                    key={row.id || `new-${idx}`}
                    sx={{
                      bgcolor: !row.id ? '#e8f5e9' : dirtyRowIds.has(row.id) ? '#fff8e1' : (idx % 2 === 0 ? '#ffffff' : '#f0f4f8'),
                      '&:hover': { bgcolor: !row.id ? '#c8e6c9' : 'rgba(25, 118, 210, 0.08)' },
                    }}
                  >
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-0`] = el; }} sx={getCellStyle(idx, 0, row)}>
                      <TextField
                        size="small"
                        fullWidth
                        variant="standard"
                        value={row.name}
                        onChange={(e) => handleCellChange(idx, 'name', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 0 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 0, e)}
                        placeholder="Product name"
                        InputProps={{ disableUnderline: true }}
                        sx={cellFieldSx}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-1`] = el; }} sx={getCellStyle(idx, 1, row)}>
                      <TextField
                        size="small"
                        fullWidth
                        variant="standard"
                        value={row.hsnCode}
                        onChange={(e) => handleCellChange(idx, 'hsnCode', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 1 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 1, e)}
                        placeholder=""
                        InputProps={{ disableUnderline: true }}
                        sx={cellFieldSx}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-2`] = el; }} sx={getCellStyle(idx, 2, row)}>
                      <FormControl size="small" fullWidth variant="standard" sx={selectSx} onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 2 })} onKeyDown={(e) => handleCellKeyDown(idx, 2, e)}>
                        <Select
                          value={row.categoryId === '' || row.categoryId == null ? '' : Number(row.categoryId)}
                          onChange={(e) => handleDropdownChange(idx, 2, 'categoryId', e.target.value ?? '')}
                          open={openDropdown === `${idx}-2`}
                          onOpen={() => { const k = `${idx}-2`; openDropdownRef.current = k; setOpenDropdown(k); focusListboxWhenOpen(); setFocusedCell({ rowIdx: idx, colIdx: 2 }); }}
                          onClose={() => handleDropdownClose(idx, 2)}
                          displayEmpty
                          IconComponent={KeyboardArrowDownIcon}
                          InputProps={{ disableUnderline: true }}
                          MenuProps={{ autoFocus: true, disableScrollLock: true }}
                        >
                          <MenuItem value="">Select</MenuItem>
                          {categories.map((c) => (
                            <MenuItem key={c.id} value={Number(c.id)}>
                              {c.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-3`] = el; }} sx={getCellStyle(idx, 3, row)}>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={row.quantity}
                        onChange={(e) => handleCellChange(idx, 'quantity', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 3 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 3, e)}
                        onWheel={handleNumberWheel}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ disableUnderline: true }}
                        sx={{ ...noSpinnerSx, ...cellFieldSx }}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-4`] = el; }} sx={getCellStyle(idx, 4, row)}>
                      <FormControl size="small" fullWidth variant="standard" sx={selectSx} onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 4 })} onKeyDown={(e) => handleCellKeyDown(idx, 4, e)}>
                        <Select
                          value={row.unitId === '' || row.unitId == null ? '' : Number(row.unitId)}
                          onChange={(e) => handleDropdownChange(idx, 4, 'unitId', e.target.value ?? '')}
                          open={openDropdown === `${idx}-4`}
                          onOpen={() => { const k = `${idx}-4`; openDropdownRef.current = k; setOpenDropdown(k); focusListboxWhenOpen(); setFocusedCell({ rowIdx: idx, colIdx: 4 }); }}
                          onClose={() => handleDropdownClose(idx, 4)}
                          displayEmpty
                          IconComponent={KeyboardArrowDownIcon}
                          InputProps={{ disableUnderline: true }}
                          MenuProps={{ autoFocus: true, disableScrollLock: true }}
                          renderValue={(v) => {
                            if (!v) return '';
                            const u = units.find((x) => x.id != null && String(x.id) === String(v));
                            return u ? (u.abbreviation || u.name) : '';
                          }}
                        >
                          <MenuItem value="">-</MenuItem>
                          {units.map((u) => (
                            <MenuItem key={u.id} value={Number(u.id)}>
                              {u.abbreviation}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-5`] = el; }} sx={getCellStyle(idx, 5, row)}>
                      <FormControl size="small" fullWidth variant="standard" sx={selectSx} onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 5 })} onKeyDown={(e) => handleCellKeyDown(idx, 5, e)}>
                        <Select
                          value={row.taxId === '' || row.taxId == null ? '' : Number(row.taxId)}
                          onChange={(e) => handleDropdownChange(idx, 5, 'taxId', e.target.value ?? '')}
                          open={openDropdown === `${idx}-5`}
                          onOpen={() => { const k = `${idx}-5`; openDropdownRef.current = k; setOpenDropdown(k); focusListboxWhenOpen(); setFocusedCell({ rowIdx: idx, colIdx: 5 }); }}
                          onClose={() => handleDropdownClose(idx, 5)}
                          displayEmpty
                          IconComponent={KeyboardArrowDownIcon}
                          InputProps={{ disableUnderline: true }}
                          MenuProps={{ autoFocus: true, disableScrollLock: true }}
                        >
                          <MenuItem value="">None</MenuItem>
                          {taxes.map((t) => (
                            <MenuItem key={t.id} value={Number(t.id)}>
                              {getTaxDropdownLabelWithRate(t)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-6`] = el; }} sx={getCellStyle(idx, 6, row)}>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={row.mrp}
                        onChange={(e) => handleCellChange(idx, 'mrp', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 6 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 6, e)}
                        onWheel={handleNumberWheel}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ disableUnderline: true }}
                        sx={{ ...noSpinnerSx, ...cellFieldSx }}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-7`] = el; }} sx={getCellStyle(idx, 7, row)}>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={row.purchasePrice}
                        onChange={(e) => handleCellChange(idx, 'purchasePrice', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 7 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 7, e)}
                        onWheel={handleNumberWheel}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ disableUnderline: true }}
                        sx={{ ...noSpinnerSx, ...cellFieldSx }}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-8`] = el; }} sx={getCellStyle(idx, 8, row)}>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={row.sellingPrice}
                        onChange={(e) => handleCellChange(idx, 'sellingPrice', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 8 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 8, e)}
                        onWheel={handleNumberWheel}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ disableUnderline: true }}
                        sx={{ ...noSpinnerSx, ...cellFieldSx }}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-9`] = el; }} sx={getCellStyle(idx, 9, row)}>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={row.sellingPriceWithTax}
                        onChange={(e) => handleCellChange(idx, 'sellingPriceWithTax', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 9 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 9, e)}
                        onWheel={handleNumberWheel}
                        inputProps={{ min: 0, step: 0.01 }}
                        placeholder="0"
                        InputProps={{ disableUnderline: true }}
                        sx={{ ...noSpinnerSx, ...cellFieldSx }}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-10`] = el; }} sx={getCellStyle(idx, 10, row)}>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={row.wholesalePrice}
                        onChange={(e) => handleCellChange(idx, 'wholesalePrice', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 10 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 10, e)}
                        onWheel={handleNumberWheel}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ disableUnderline: true }}
                        sx={{ ...noSpinnerSx, ...cellFieldSx }}
                      />
                    </StyledTableCell>
                    <StyledTableCell ref={(el) => { if (el) cellRefs.current[`${idx}-11`] = el; }} sx={getCellStyle(idx, 11, row)}>
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        variant="standard"
                        value={row.wholesalePriceWithTax}
                        onChange={(e) => handleCellChange(idx, 'wholesalePriceWithTax', e.target.value)}
                        onFocus={() => setFocusedCell({ rowIdx: idx, colIdx: 11 })}
                        onKeyDown={(e) => handleCellKeyDown(idx, 11, e)}
                         onWheel={handleNumberWheel}
                        inputProps={{ min: 0, step: 0.01 }}
                        placeholder="0"
                        InputProps={{ disableUnderline: true }}
                        sx={{ ...noSpinnerSx, ...cellFieldSx }}
                      />
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Layout>
  );
};

export default QuickProductEntryPage;
